import React from 'react';
import { useAuth } from '../context/AuthContext';
import { usePlan } from '../context/PlanContext';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  MessageSquare,
  FileText,
  BookOpen,
  Calendar,
  Trophy,
  CreditCard,
  LogOut,
  X,
  User,
  Users,
  Sparkles,
  Search,
  Shield,
  GraduationCap,
  ShieldAlert,
  Lock,
  BarChart3
} from 'lucide-react';

const DashboardSidebar = ({ activePage, setActivePage, sidebarOpen, setSidebarOpen }) => {
  const { logout, user } = useAuth();
  const { planAtLeast } = usePlan();
  const navigate = useNavigate();

  // Detect if we're in preview (not production)
  const isPreview = window.location.hostname === 'localhost' || 
                    window.location.hostname.includes('preview') ||
                    window.location.hostname.includes('127.0.0.1') ||
                    process.env.REACT_APP_BACKEND_URL?.includes('webhook-trial-debug');

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'communication', label: 'Comms', icon: MessageSquare, minPlan: 'basic' },
    { id: 'coparent-link', label: 'Co-Parent Link', icon: Users, minPlan: 'basic' },
    { id: 'journal', label: 'Journal', icon: FileText, minPlan: 'basic' },
    { id: 'calendar', label: 'Calendar', icon: Calendar, minPlan: 'basic' },
    { id: 'plan-builder', label: 'Plan Builder', icon: BookOpen, minPlan: 'basic' },
    { id: 'plan-analysis', label: 'Plan Analysis', icon: Search },
    { id: 'progress', label: 'Progress', icon: Trophy },
    ...(isPreview ? [{ id: 'analytics', label: 'Analytics', icon: BarChart3 }] : []), // Only in preview
    { id: 'courses', label: 'Courses', icon: GraduationCap },
    ...(user?.role === 'admin' ? [{ id: 'admin', label: 'Admin Panel', icon: ShieldAlert }] : []),
    { id: 'privacy', label: 'Privacy Center', icon: Shield },
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'settings', label: 'Billing', icon: CreditCard },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <>
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`theme-dark fixed lg:sticky top-0 left-0 h-screen w-64 bg-[#2D2560] border-r border-white/10 z-50 transform transition-transform duration-300 flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="dashboard-sidebar"
      >
        <div className="p-4 sm:p-6 border-b border-white/10 flex items-center justify-between flex-shrink-0">
          <a href="/" data-testid="sidebar-logo-link">
            <img 
              src="/parentum-mark.png" 
              alt="Parentum" 
              className="h-8 sm:h-10 w-auto cursor-pointer hover:opacity-80 transition-opacity"
            />
          </a>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-white/60 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <nav className="p-2 sm:p-4 space-y-0.5 sm:space-y-1 flex-1 overflow-y-auto min-h-0">
          {menuItems.map((item) => {
            var locked = item.minPlan && !planAtLeast(item.minPlan);
            var poLocked = user?.protective_order_active && (item.id === 'communication' || item.id === 'coparent-link');
            return (
            <button
              key={item.id}
              onClick={() => {
                if (poLocked) return;
                setActivePage(item.id);
                setSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg transition-colors text-sm ${
                poLocked ? 'text-[#FCA5A5] opacity-60 cursor-not-allowed' :
                activePage === item.id
                  ? 'bg-[#1A1738] text-white border border-[#1A1738] shadow-sm'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
              data-testid={`sidebar-${item.id}`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">{item.label}</span>
              {poLocked && <Shield className="w-3.5 h-3.5 text-[#EF4444]/40 ml-auto flex-shrink-0" />}
              {!poLocked && locked && <Lock className="w-3.5 h-3.5 text-white/20 ml-auto flex-shrink-0" />}
            </button>
          );})}
        </nav>

        <div className="p-2 sm:p-4 border-t border-white/10 flex-shrink-0 space-y-2">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-white/60 hover:text-white hover:bg-white/5 transition-colors text-sm"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <span>Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default DashboardSidebar;
